import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  SimpleForm,
  Edit,
  TextInput,
  BooleanInput,
  SaveButton,
  DeleteWithConfirmButton,
  Toolbar,
  Notification,
  useNotify,
  useRefresh,
  NumberInput,
  FormDataConsumer,
} from "react-admin";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import { ThemeProvider } from "@material-ui/styles";
import { useForm } from "react-final-form";
import axios from "axios";
import customTheme from "../../customTheme";
import { apiUrl } from "../../dataProvider";
import {
  validateMagicNUmber,
  validateRequired,
  valueBetween,
} from "../../utils/Validations";
import WithProps from "../Custom/WithProps";
import CustomAdminSelect from "./CustomAdminSelect";
import VideoPreview from "./VideoPreview";

export const normalizeActiveValue = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  if (value === true || value === 1 || value === "1") {
    return true;
  }
  if (value === false || value === 0 || value === "0") {
    return false;
  }
  return Boolean(value);
};

export const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

export const parseMagicNumberInput = (value) => {
  if (typeof value !== "string") return value;
  return value.trim();
};

export const trimSimilarRings = (rings) => {
  if (!Array.isArray(rings)) return rings;
  return rings.map((ring) => parseMagicNumberInput(ring));
};

export const transformBestsellerSave = (data) => ({
  ...data,
  // Send null (not an omitted/empty value) so the backend clears the video in db.
  video_url: isEmptyValue(data?.video_url) ? null : data.video_url,
  similar_rings: trimSimilarRings(data?.similar_rings),
  builder_platform_data: data?.builder_platform_data
    ? {
        ...data.builder_platform_data,
        similar_rings: trimSimilarRings(
          data.builder_platform_data.similar_rings,
        ),
      }
    : data?.builder_platform_data,
});

const useStyles = makeStyles(() => ({
  root: {
    width: "90%",
    margin: "auto",
  },
  toolbar: {
    justifyContent: "space-between",
  },
  grouping: {
    border: "1px solid lightgray",
    borderRadius: "3px",
    margin: "1% auto",
  },
  warnLabel: {
    fontSize: "10px",
    color: "red",
    display: "block",
  },
  displayFlex: {
    display: "flex",
  },
  equalWidth: {
    maxWidth: "33%",
  },
  heading: {
    fontWeight: "bold",
    marginTop: "10px",
    borderBottom: "3px solid #a6a5a5ff",
    paddingBottom: "5px",
  },
  builderDeleteCell: {
    display: "flex",
    alignItems: "center",
    paddingTop: 8,
  },
  builderInfoCard: {
    padding: "12px 16px",
    marginBottom: 8,
    backgroundColor: "#f5f7fa",
    border: "1px solid #e0e4e8",
    borderLeft: "4px solid #1976d2",
    borderRadius: 4,
    position: "relative",
  },
  builderInfoTitle: {
    fontWeight: 600,
    marginBottom: 8,
    color: "#1a1a1a",
    paddingRight: 180,
  },
  builderInfoList: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px 24px",
    margin: "0 0 0 12px",
    paddingLeft: 0,
    "& li": {
      margin: 0,
      maxWidth: "100%",
      wordBreak: "break-word",
    },
  },
  copySimilarRingsButtonWrap: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1,
  },
  builderCopyButtonBar: {
    position: "relative",
    minHeight: 40,
    marginBottom: 8,
  },
  similarRingRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  similarRingInput: {
    flex: 1,
    minWidth: 0,
  },
  similarRingPreview: {
    width: 48,
    height: 48,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  similarRingThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid #e0e4e8",
    cursor: "pointer",
    backgroundColor: "#fff",
  },
  similarRingThumbPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 4,
    border: "1px dashed #cfd6dd",
    color: "#cfd6dd",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  previewTooltip: {
    maxWidth: 240,
    backgroundColor: "#ffffff",
    color: "#1a1a1a",
    border: "1px solid #e0e4e8",
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
    padding: 12,
  },
  previewTooltipImage: {
    width: "100%",
    maxWidth: 180,
    display: "block",
    margin: "0 auto 8px",
    borderRadius: 4,
  },
  previewPrice: {
    fontWeight: 700,
    textAlign: "center",
  },
  previewHighlights: {
    margin: "8px 0 0",
    paddingLeft: 18,
    "& li": {
      marginBottom: 2,
    },
  },
}));

const EditToolbar = (props) => {
  const classes = useStyles();
  return (
    <Toolbar {...props} className={classes.toolbar}>
      <SaveButton disabled={props.pristine} />
      <DeleteWithConfirmButton />
    </Toolbar>
  );
};

EditToolbar.propTypes = {
  pristine: PropTypes.bool,
};

const BuilderPlatformDataSync = ({ bestsellerId, builderPlatformId }) => {
  const form = useForm();
  const notify = useNotify();
  const previousBuilderPlatformIdRef = useRef(builderPlatformId);

  useEffect(() => {
    const previousBuilderPlatformId = previousBuilderPlatformIdRef.current;
    previousBuilderPlatformIdRef.current = builderPlatformId;

    if (builderPlatformId === previousBuilderPlatformId) {
      return;
    }

    if (!builderPlatformId) {
      form.change("builder_platform_data", {});
      return;
    }

    const fetchBuilderPlatformData = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/bestsellers/${bestsellerId}?builder_platform_id=${builderPlatformId}`,
        );

        const builderPlatformData = response?.data?.data?.builder_platform_data;
        if (builderPlatformData) {
          form.change("builder_platform_data", builderPlatformData);
        } else {
          form.change("builder_platform_data", {
            builder_platform_id: builderPlatformId,
            active: 0,
            discount_percentage: null,
            similar_rings: [],
          });
        }
      } catch (error) {
        notify(
          error?.response?.data?.message ||
            "Failed to fetch builder platform data",
          { type: "warning" },
        );
      }
    };

    fetchBuilderPlatformData();
  }, [builderPlatformId, bestsellerId, form, notify]);

  return null;
};

BuilderPlatformDataSync.propTypes = {
  bestsellerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const BuilderPlatformBestsellerDeleteButton = ({ joinId }) => {
  const form = useForm();
  const notify = useNotify();
  const refresh = useRefresh();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (isEmptyValue(joinId) || loading) {
      return;
    }
    setLoading(true);
    try {
      await axios.delete(`${apiUrl}/builder_platform_bestseller/${joinId}`);
      notify("Builder platform removed", { type: "success" });
      form.change("builder_platform_data", {});
      refresh();
    } catch (error) {
      notify(
        error?.response?.data?.message || "Failed to remove builder platform",
        { type: "warning" },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.builderDeleteCell}>
      <IconButton
        aria-label="Remove builder platform"
        onClick={handleClick}
        disabled={loading}
        size="small"
      >
        {loading ? <CircularProgress size={20} /> : <DeleteIcon />}
      </IconButton>
    </div>
  );
};

BuilderPlatformBestsellerDeleteButton.propTypes = {
  joinId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// Per-environment token, configured via .env / .env.staging / .env.production.
const MAGIC_NUMBER_PRICE_API_TOKEN =
  process.env.REACT_APP_MAGIC_NUMBER_PRICE_API_TOKEN;

const SimilarRingPreview = ({ info }) => {
  const classes = useStyles();
  if (!info) {
    return (
      <span className={classes.similarRingPreview}>
        <span className={classes.similarRingThumbPlaceholder}>
          <ImageOutlinedIcon fontSize="small" />
        </span>
      </span>
    );
  }

  const {
    image_preview_url: imageUrl,
    unit_price: unitPrice,
    total_amount: totalAmount,
    highlights = [],
    title,
    name,
  } = info;
  const price = unitPrice || totalAmount;
  const altText = title || name || "ring preview";

  const tooltipContent = (
    <div>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={altText}
          className={classes.previewTooltipImage}
        />
      ) : null}
      {price ? (
        <Typography variant="h6" className={classes.previewPrice}>
          {price}
        </Typography>
      ) : null}
      {Array.isArray(highlights) && highlights.length > 0 ? (
        <ul className={classes.previewHighlights}>
          {highlights.map((highlight, index) => (
            <li key={`${highlight}-${index}`}>{highlight}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );

  return (
    <Tooltip
      interactive
      placement="left"
      classes={{ tooltip: classes.previewTooltip }}
      title={tooltipContent}
    >
      <span className={classes.similarRingPreview}>
        {imageUrl ? (
          <img src={imageUrl} alt={altText} className={classes.similarRingThumb} />
        ) : (
          <span className={classes.similarRingThumbPlaceholder}>
            <ImageOutlinedIcon fontSize="small" />
          </span>
        )}
      </span>
    </Tooltip>
  );
};

SimilarRingPreview.propTypes = {
  info: PropTypes.shape({
    image_preview_url: PropTypes.string,
    unit_price: PropTypes.string,
    total_amount: PropTypes.string,
    highlights: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    name: PropTypes.string,
  }),
};

export const SimilarRingsWithPreview = ({ similarRings, sourcePrefix, count }) => {
  const classes = useStyles();
  const [priceByMagicNumber, setPriceByMagicNumber] = useState({});

  const ringsKey = JSON.stringify(
    (Array.isArray(similarRings) ? similarRings : [])
      .map((ring) => parseMagicNumberInput(ring))
      .filter((ring) => !isEmptyValue(ring)),
  );

  useEffect(() => {
    const rings = JSON.parse(ringsKey);

    if (rings.length === 0) {
      setPriceByMagicNumber({});
      return undefined;
    }

    let isMounted = true;

    const fetchMagicNumberPrice = async () => {
      try {
        const response = await axios.post(`${apiUrl}/fetchMagicNumberPrice`, {
          api_token: MAGIC_NUMBER_PRICE_API_TOKEN,
          send_highlights: true,
          data: rings.map((ring) => `${ring},Retail`),
        });

        if (!isMounted) return;

        const responseData = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(responseData)
          ? responseData
          : responseData && typeof responseData === "object"
            ? Object.values(responseData)
            : [];
        const map = list.reduce((acc, item) => {
          if (item && typeof item === "object" && item.magic_number) {
            acc[parseMagicNumberInput(item.magic_number)] = item;
          }
          return acc;
        }, {});
        setPriceByMagicNumber(map);
        // console.log("fetchMagicNumberPrice response:", responseData);
        // console.log("fetchMagicNumberPrice matched keys:", Object.keys(map));
      } catch (error) {
        if (!isMounted) return;
        // eslint-disable-next-line no-console
        // console.error("fetchMagicNumberPrice failed:", error);
      }
    };

    const timeoutId = setTimeout(fetchMagicNumberPrice, 600);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [ringsKey]);

  const getInfoForValue = (value) => {
    if (isEmptyValue(value)) {
      return null;
    }
    if (priceByMagicNumber[value]) {
      return priceByMagicNumber[value];
    }
    // The API appends the ring size to the magic number (e.g. "000-B4A1-10"),
    // while the input only stores the base ("000-B4A1"), so fall back to a
    // prefix match on the base magic number.
    const matchedKey = Object.keys(priceByMagicNumber).find(
      (key) => key === value || key.startsWith(`${value}-`),
    );
    return matchedKey ? priceByMagicNumber[matchedKey] : null;
  };

  return (
    <>
      {Array.from({ length: count }, (_, index) => index + 1).map(
        (ringPosition) => {
          const value = parseMagicNumberInput(
            similarRings?.[ringPosition - 1],
          );
          const info = getInfoForValue(value);

          return (
            <div
              key={`${sourcePrefix}-${ringPosition}`}
              className={classes.similarRingRow}
            >
              <div className={classes.similarRingInput}>
                <TextInput
                  source={`${sourcePrefix}[${ringPosition - 1}]`}
                  label={`Similar Ring ${ringPosition}`}
                  validate={validateMagicNUmber}
                  parse={parseMagicNumberInput}
                  fullWidth
                />
              </div>
              <SimilarRingPreview info={info} />
            </div>
          );
        },
      )}
    </>
  );
};

SimilarRingsWithPreview.propTypes = {
  similarRings: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  sourcePrefix: PropTypes.string,
  count: PropTypes.number,
};

SimilarRingsWithPreview.defaultProps = {
  sourcePrefix: "similar_rings",
  count: 5,
};

export const CopySimilarRingsButton = () => {
  const form = useForm();
  const notify = useNotify();

  const handleCopy = () => {
    const { similar_rings: defaultRings = [] } = form.getState().values;

    [0, 1, 2, 3, 4].forEach((index) => {
      form.change(
        `builder_platform_data.similar_rings[${index}]`,
        parseMagicNumberInput(defaultRings[index] ?? ""),
      );
    });

    notify("Similar rings copied from default profile", { type: "info" });
  };

  return (
    <Button variant="contained" color="primary" onClick={handleCopy}>
      Copy Similar Rings
    </Button>
  );
};

const EditBestSeller = (props) => {
  const classes = useStyles();

  const notify = useNotify();
  const onFailure = (error) => {
    notify(
      error.response.data.message
        ? error.response.data.message
        : "Something went wrong",
      { type: "warning" },
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Edit
        mutationMode="pessimistic"
        onFailure={onFailure}
        {...props}
        transform={transformBestsellerSave}
      >
        <WithProps>
          {({ record, ...props }) => (
            <SimpleForm
              record={record}
              {...props}
              redirect="list"
              toolbar={<EditToolbar />}
              sanitizeEmptyValues={false}
            >
              <Grid
                container
                spacing={2}
                className={classes.root}
                justify="space-between"
              >
                <Grid
                  item
                  container
                  xs={12}
                  className={classes.displayFlex}
                  justify="space-between"
                  alignItems="center"
                >
                  <Grid item xs={4}>
                    <TextInput
                      source="magic_number"
                      label="Magic Number"
                      validate={validateRequired}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <NumberInput
                      source="discount_percentage"
                      label="Discount Percentage"
                      validate={[validateRequired, valueBetween(0, 100)]}
                      fullWidth
                    />
                  </Grid>
                  <FormDataConsumer>
                    {({ formData }) => {
                      const activeValue = formData?.active;
                      const isActive =
                        activeValue === undefined
                          ? (record?.active ?? true)
                          : Boolean(activeValue);

                      return (
                        <Grid item xs={2}>
                          <BooleanInput
                            label={isActive ? "Active" : "Inactive"}
                            source="active"
                            initialValue={true}
                          />
                        </Grid>
                      );
                    }}
                  </FormDataConsumer>
                  <Grid item xs={4}>
                    <CustomAdminSelect
                      label="Video URL"
                      source="video_url"
                      resource="getBestsellerVideoList"
                    />
                  </Grid>
                  <Grid item container xs={12} spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <BooleanInput
                        label="Hide Quarter Sizes"
                        source="hide_quarter_sizes"
                        initialValue={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <BooleanInput
                        label="Hide Outside Features In Highlights"
                        source="hide_outside_features_in_highlights"
                        initialValue={false}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <BooleanInput
                        label="Hide Inside Features In Highlights"
                        source="hide_inside_features_in_highlights"
                        initialValue={false}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <VideoPreview />
                <Grid item xs={5}>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <SimilarRingsWithPreview
                        similarRings={formData?.similar_rings}
                        sourcePrefix="similar_rings"
                        count={5}
                      />
                    )}
                  </FormDataConsumer>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                className={classes.root}
                alignItems="center"
                justify="space-between"
              >
                <Grid item container xs={12} className={classes.heading}>
                  Builder Platform Configuration
                </Grid>
                {Array.isArray(record?.builder_platforms) &&
                record.builder_platforms.length > 0 ? (
                  <Grid item xs={12}>
                    <Paper className={classes.builderInfoCard} elevation={0}>
                      <FormDataConsumer>
                        {({ formData }) => {
                          const builderPlatformId =
                            formData?.builder_platform_data
                              ?.builder_platform_id;
                          const showCopyButton =
                            !isEmptyValue(builderPlatformId);

                          return showCopyButton ? (
                            <div className={classes.copySimilarRingsButtonWrap}>
                              <CopySimilarRingsButton />
                            </div>
                          ) : null;
                        }}
                      </FormDataConsumer>
                      <Typography
                        variant="subtitle2"
                        component="h3"
                        className={classes.builderInfoTitle}
                      >
                        Builders with custom configuration
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        color="textSecondary"
                      >
                        <ul className={classes.builderInfoList}>
                          {record.builder_platforms.map((label, index) => (
                            <li key={`${label}-${index}`}>{label}</li>
                          ))}
                        </ul>
                      </Typography>
                    </Paper>
                  </Grid>
                ) : null}
                <FormDataConsumer>
                  {({ formData }) => {
                    const builderPlatformId =
                      formData?.builder_platform_data?.builder_platform_id;
                    const showCopyButton = !isEmptyValue(builderPlatformId);
                    const hasBuilderInfoCard =
                      Array.isArray(record?.builder_platforms) &&
                      record.builder_platforms.length > 0;

                    return showCopyButton && !hasBuilderInfoCard ? (
                      <Grid
                        item
                        xs={12}
                        className={classes.builderCopyButtonBar}
                      >
                        <div className={classes.copySimilarRingsButtonWrap}>
                          <CopySimilarRingsButton />
                        </div>
                      </Grid>
                    ) : null;
                  }}
                </FormDataConsumer>
                <Grid item xs={3}>
                  <CustomAdminSelect
                    label="Builder Platform"
                    source="builder_platform_data.builder_platform_id"
                    resource="builder-platform?page[number]=1&page[size]=1000"
                    optionLabel="store_name"
                  />
                </Grid>
                <FormDataConsumer>
                  {({ formData }) => {
                    const { builder_platform_id, id, active } =
                      formData?.builder_platform_data || {};
                    const isBuilderActive = normalizeActiveValue(active, false);
                    const showBuilderFields =
                      !isEmptyValue(builder_platform_id);
                    const hasBuilderJoinId = isEmptyValue(id) === false;

                    return (
                      <>
                        <BuilderPlatformDataSync
                          bestsellerId={record?.id}
                          builderPlatformId={builder_platform_id}
                        />
                        {showBuilderFields ? (
                          <>
                            <Grid item xs={2}>
                              <BooleanInput
                                label={isBuilderActive ? "Active" : "Inactive"}
                                source="builder_platform_data.active"
                                format={(value) =>
                                  normalizeActiveValue(value, false)
                                }
                                parse={(value) => (value ? 1 : 0)}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <NumberInput
                                source="builder_platform_data.discount_percentage"
                                label="Builder Discount Percentage"
                                validate={
                                  isBuilderActive
                                    ? [validateRequired, valueBetween(0, 100)]
                                    : [valueBetween(0, 100)]
                                }
                                fullWidth
                              />
                            </Grid>
                            {hasBuilderJoinId ? (
                              <Grid item xs={1}>
                                <BuilderPlatformBestsellerDeleteButton
                                  joinId={id}
                                />
                              </Grid>
                            ) : null}
                            <Grid item xs={4}>
                              <SimilarRingsWithPreview
                                similarRings={
                                  formData?.builder_platform_data?.similar_rings
                                }
                                sourcePrefix="builder_platform_data.similar_rings"
                                count={5}
                              />
                            </Grid>
                          </>
                        ) : null}
                      </>
                    );
                  }}
                </FormDataConsumer>
              </Grid>
            </SimpleForm>
          )}
        </WithProps>
      </Edit>
      <Notification />
    </ThemeProvider>
  );
};

export default EditBestSeller;
